import React from 'react'
import TimeOffBalance from '../TimeOffBalance'
import { EmployeeInfo } from '../EmployeeInfo'
import BreadCrumbs from '../../../components/common/BreadCrumbs/BreadCrumbs'
import AddTimeToRequest from '../CreateRequest/AddTimeToRequest'
import EditAndResubmitVacationBid from '../CreateRequest/EditAndResubmitVacationBid'
import Datepicker from '../../../components/common/DatePicker/Datepicker'
import ValidationBanner from '../../../components/common/ValidationBanner'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  getTimeOffDetails,
  isVacationBid,
} from '../../../store/slices/Requestor/timeOffDetailsSlice'
import { useOtherRequestDetails } from './detail/useOtherRequestDetails'
import Loader from '../../../components/common/Loader'
import {
  parseISOTimeString,
  parseTimeString,
} from '../../../utils/timeStringParser'
import { resolveRequestedDateDuration } from '../../../utils/durationUtils'
import { isPastDate, convertMMDDYYYYToYYYYMMDD } from '../../../utils/date'
import ManualTimeOffBanner from '../../../components/common/ManualTimeOffBanner'
import { mapCommonValidationMessage } from '../validation/mapCommonValidationMessage'
import ValidationMessages from '../../../components/common/Validation/validationMessageConfig'
import ColorLegendTooltip from '../../../components/common/ColorLegendTooltip'

type ValidationMapResult = {
  variant?: 'info' | 'error'
  heading?: JSX.Element
  showMessage?: boolean
  messageNode?: JSX.Element
}

const mapEditSubmittedValidationMessage = (
  text: string,
  onFocusOtherSubtype: () => void
): ValidationMapResult => {
  const commonValidation = mapCommonValidationMessage(text, onFocusOtherSubtype)
  if (commonValidation) {
    return commonValidation
  }

  if (text === 'Resubmitted comments are required') {
    return {
      heading: (
        <p className="p1 -medium m-0!">Resubmitted Comments are required</p>
      ),
    }
  }

  // Emphasize total-hours banner for edit & resubmit flow
  if (text === ValidationMessages.TOTAL_HOURS_EXCEED_MAX) {
    return {
      heading: (
        <p className="p1 -medium m-0!">
          <strong>{ValidationMessages.TOTAL_HOURS_EXCEED_MAX}</strong>
        </p>
      ),
    }
  }

  return {
    showMessage: true,
    messageNode: <p className="p3 -book m-0!">{text}</p>,
  }
}

const renderEditSubmittedValidationBanners = (
  validationErrors: string[],
  onFocusOtherSubtype: () => void
) => (
  <div className="space-y-3 mb-4 mt-8!">
    {validationErrors.map((msg, idx) => {
      const { heading, showMessage, messageNode, variant } =
        mapEditSubmittedValidationMessage(msg, onFocusOtherSubtype)
      const bannerKey =
        typeof msg === 'string' && msg.length > 0 ? msg : `validation-${idx}`

      return (
        <ValidationBanner
          key={bannerKey}
          variant={variant ?? 'error'}
          {...(heading ? { heading } : {})}
          {...(showMessage && messageNode ? { message: messageNode } : {})}
          compact
        />
      )
    })}
  </div>
)

/**
 * Maps a time-off type code to the corresponding hours bucket key.
 * Returns null for unrecognized codes.
 */
const mapTypeCodeToHoursKey = (
  code: string
): 'vacation' | 'float' | 'flexible' | 'other' | null => {
  switch (code) {
    case 'V':
    case 'PTO':
    case 'ETO':
      return 'vacation'
    case 'F':
      return 'float'
    case 'D':
    case 'X':
      return 'flexible'
    case 'O':
      return 'other'
    default:
      return null
  }
}

/**
 * EditSubmittedRequest component
 * Displays the second step (review + calendar) of CreateNewRequest
 * User can edit existing dates or add new dates to raise another request
 * Uses Redux to fetch and manage request data
 */
const EditSubmittedRequest: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isAdminMode = location.pathname.includes('/admin')
  const { requestId } = useParams<{ requestId?: string }>()
  const dispatch = useAppDispatch()

  // Get timeOffDetails from Redux
  const timeOffDetails = useAppSelector((state) => state.timeOffDetails?.data)
  const loading = useAppSelector((state) => state.timeOffDetails?.loading)
  const error = useAppSelector((state) => state.timeOffDetails?.error)
  const profileEmpType = useAppSelector((state) => {
    const profile = state.profile as
      | { data?: { employee?: { empType?: string } } }
      | undefined
    return profile?.data?.employee?.empType
  })
  // Scoped to the employee the request is for (x-emp-nuid), so this is the
  // selected employee in Manual Time Off rather than the logged-in admin.
  const employeeEmpType = useAppSelector(
    (state) => state.employeeInfo?.employeeInfo?.data?.empType
  )
  const isExempt = (employeeEmpType ?? profileEmpType) === 'S'

  // Fetch request details on mount
  React.useEffect(() => {
    if (requestId) {
      dispatch(getTimeOffDetails(requestId))
    }
  }, [requestId, dispatch])

  // Build selected dates from Redux data
  const selectedDates = React.useMemo(() => {
    if (!timeOffDetails?.requestedDates) return []
    // Convert from YYYY-MM-DD to MM/DD/YYYY
    return timeOffDetails.requestedDates.map((d) => {
      const [year, month, day] = d.date.split('-')
      return `${month}/${day}/${year}`
    })
  }, [timeOffDetails?.requestedDates])

  // Use hook to get prepopulated leaves (green calendar highlights).
  // Excludes this request's own id — the other-requests-for-month data
  // includes every request on a date, including the one being edited, so
  // without this its own hours were counted as "other" hours against itself.
  const {
    otherRequestDates,
    otherRequestDetailsForTooltip,
    otherRequestHoursByDate,
    handleMonthYearChange,
  } = useOtherRequestDetails(selectedDates, false, requestId)

  /**
   * Map of MM/DD/YYYY dates that have another user's partial-day request, keyed
   * to an array of ALL their time ranges on that date. Passed to AddTimeToRequest
   * so it can force Partial Day (disable the Full Day radio) and check for time
   * overlaps against every existing partial request, not just the first one.
   * Mirrors CreateNewRequest's identically-named/-built otherPartialDayDates.
   *
   * Regression: this prop was never built/passed here, so on Edit and Resubmit
   * the Full Day radio stayed selectable even on a date that already had
   * another partial-day request, and performPartialDayOverlapCheck (which
   * early-returns when otherPartialDayDates is undefined) never ran.
   */
  const otherPartialDayDates = React.useMemo(() => {
    const result: Record<
      string,
      Array<{ startTime?: string | null; endTime?: string | null }>
    > = {}
    Object.entries(otherRequestDetailsForTooltip ?? {}).forEach(
      ([dateKey, entry]) => {
        if (entry.durationLabel === 'Partial Day') {
          const [yr, mo, dy] = dateKey.split('-')
          const mmddyyyy = `${mo}/${dy}/${yr}`
          const ranges: Array<{
            startTime?: string | null
            endTime?: string | null
          }> =
            entry.individualRequests && entry.individualRequests.length > 0
              ? entry.individualRequests.map((req) => ({
                  startTime: req.startTime ?? null,
                  endTime: req.endTime ?? null,
                }))
              : [
                  {
                    startTime: entry.startTime ?? null,
                    endTime: entry.endTime ?? null,
                  },
                ]
          result[mmddyyyy] = ranges
        }
      }
    )
    return result
  }, [otherRequestDetailsForTooltip])

  // Build per-date hours storage from Redux data
  // IMPORTANT: Use ref to keep stable across renders so AddTimeToRequest doesn't reinitialize on every prop change
  const initialPerDateHoursStorageRef = React.useRef<Record<
    string,
    any
  > | null>(null)
  // Guarding on timeOffDetails too (not just !ref.current) matters: requestId's
  // fetch resolves asynchronously, so the first render always happens before
  // timeOffDetails is populated. Without this, the block below would run once
  // with requestedDates still undefined, lock the ref to an empty {} forever
  // (an empty object is truthy, so !ref.current never re-fires), and every
  // date's hours would return `undefined` from this storage for the rest of
  // the session — which is exactly what made handleNextOrUpdateClick's backfill
  // treat every existing date as unset and overwrite it with the actively
  // edited date's hours.
  if (!initialPerDateHoursStorageRef.current && timeOffDetails?.requestedDates) {
    const storage: Record<string, any> = {}
    timeOffDetails.requestedDates.forEach((d) => {
      const [year, month, day] = d.date.split('-')
      const dateKey = `${month}/${day}/${year}`

      // Initialize all leave types to 0
      const bucket: Record<string, string> = {
        vacation: '0',
        float: '0',
        flexible: '0',
        other: '0',
      }

      // If timeOffAllocations exists, map hours from each allocation by its type code
      if (d.timeOffAllocations && d.timeOffAllocations.length > 0) {
        d.timeOffAllocations.forEach((allocation) => {
          const key = mapTypeCodeToHoursKey(allocation.toTypeCd || '')
          if (key) bucket[key] = allocation.hours?.toString() || '0'
        })
      } else {
        // Fallback: use single requestType if allocations not available
        const key = mapTypeCodeToHoursKey(d.requestType || '')
        if (key) bucket[key] = (Number.parseFloat(d.requestedHours as any) || 0).toString()
      }

      storage[dateKey] = bucket
    })
    initialPerDateHoursStorageRef.current = storage
  }
  const initialPerDateHoursStorage = initialPerDateHoursStorageRef.current ?? undefined

  // Build per-date duration storage from Redux data
  // IMPORTANT: Use ref to keep stable across renders
  const initialPerDateDurationStorageRef = React.useRef<Record<
    string,
    string
  > | null>(null)
  if (!initialPerDateDurationStorageRef.current && timeOffDetails?.requestedDates) {
    const storage: Record<string, string> = {}
    timeOffDetails.requestedDates.forEach((d) => {
      const [year, month, day] = d.date.split('-')
      const dateKey = `${month}/${day}/${year}`

      // Full/Partial comes from the authoritative `durationFlag`. A Full Day
      // can still carry a start/end time window (e.g. a 4-hour full day), so
      // inferring the duration from `times`/`startTime` alone previously
      // misclassified those dates as Partial Day.
      storage[dateKey] = resolveRequestedDateDuration(d)
    })
    initialPerDateDurationStorageRef.current = storage
  }
  const initialPerDateDurationStorage = initialPerDateDurationStorageRef.current ?? undefined

  // Build per-date other subtype storage from Redux data
  // IMPORTANT: Use ref to keep stable across renders
  const initialPerDateOtherSubTypeStorageRef = React.useRef<Record<
    string,
    string
  > | null>(null)
  if (!initialPerDateOtherSubTypeStorageRef.current && timeOffDetails?.requestedDates) {
    const storage: Record<string, string> = {}
    timeOffDetails.requestedDates.forEach((d) => {
      const [year, month, day] = d.date.split('-')
      const dateKey = `${month}/${day}/${year}`

      // Extract toSubTypeCd from the "O" (Other) type allocation
      if (d.timeOffAllocations && d.timeOffAllocations.length > 0) {
        const otherAllocation = d.timeOffAllocations.find(
          (a) => a.toTypeCd === 'O'
        )
        if (otherAllocation?.toSubTypeCd) {
          storage[dateKey] = otherAllocation.toSubTypeCd
        }
      } else if (d.toSubTypeCd && d.requestType === 'O') {
        // Fallback: use toSubTypeCd from RequestedDate if allocations not available
        storage[dateKey] = d.toSubTypeCd
      }
    })
    initialPerDateOtherSubTypeStorageRef.current = storage
  }
  const initialPerDateOtherSubTypeStorage =
    initialPerDateOtherSubTypeStorageRef.current ?? undefined

  // Build per-date time storage from Redux data for partial days
  // IMPORTANT: Use ref to keep stable across renders
  const initialPerDateTimeStorageRef = React.useRef<Record<string, any> | null>(
    null
  )
  if (!initialPerDateTimeStorageRef.current && timeOffDetails?.requestedDates) {
    const storage: Record<string, any> = {}
    timeOffDetails.requestedDates.forEach((d) => {
      const [year, month, day] = d.date.split('-')
      const dateKey = `${month}/${day}/${year}`

      // Try to parse from d.times first
      if (d.times && d.times !== 'Full Day') {
        const parsedTime = parseTimeString(d.times)
        if (parsedTime) {
          storage[dateKey] = parsedTime
          return
        }
      }

      // Fallback: parse from startTime and endTime if available
      if (d.startTime && d.endTime) {
        const startTimeStr = parseISOTimeString(d.startTime)
        const endTimeStr = parseISOTimeString(d.endTime)
        if (startTimeStr && endTimeStr) {
          const combinedTimeStr = `${startTimeStr} - ${endTimeStr}`
          const parsedTime = parseTimeString(combinedTimeStr)
          if (parsedTime) {
            storage[dateKey] = parsedTime
          }
        }
      }
    })
    initialPerDateTimeStorageRef.current = storage
  }
  const initialPerDateTimeStorage = initialPerDateTimeStorageRef.current ?? undefined

  const [isAddTimeReviewVisible, setIsAddTimeReviewVisible] =
    React.useState(true)
  const [exitAddTimeReviewSignal, setExitAddTimeReviewSignal] =
    React.useState(0)
  const [focusOtherSubtypeSignal, setFocusOtherSubtypeSignal] =
    React.useState(0)
  const [editMode, setEditMode] = React.useState(false)
  const [editDate, setEditDate] = React.useState<string | null>(null)
  const [validationErrors, setValidationErrors] = React.useState<
    string[] | null
  >(null)
  const [committedDates, setCommittedDates] = React.useState<string[]>([])
  const lastReviewStateRef = React.useRef(false)
  /** Dates added by the user from the calendar (not part of the original request) */
  const [additionalDates, setAdditionalDates] = React.useState<string[]>([])
  /** Original dates that have been removed by the user (soft delete) */
  const [removedOriginalDates, setRemovedOriginalDates] = React.useState<
    string[]
  >([])
  const [isAddingMoreDates, setIsAddingMoreDates] = React.useState(false)
  /** Combined original (minus removed) + newly added dates, passed to both Datepicker and AddTimeToRequest */
  const allSelectedDates = React.useMemo(() => {
    const remainingOriginal = selectedDates.filter(
      (d) => !removedOriginalDates.includes(d)
    )
    return [...remainingOriginal, ...additionalDates]
  }, [selectedDates, additionalDates, removedOriginalDates])

  // Filter out dates that belong to THIS resubmit request - only show OTHER requests as green
  const filteredOtherRequestDates = React.useMemo(() => {
    if (!otherRequestDates || otherRequestDates.length === 0) return []
    return otherRequestDates.filter(
      (date) =>
        !allSelectedDates.includes(date) && !removedOriginalDates.includes(date)
    )
  }, [otherRequestDates, allSelectedDates, removedOriginalDates])

  // Filter tooltip details down to the other-request dates actually shown on the
  // calendar (filteredOtherRequestDates). Without this, Datepicker's half-filled
  // "partial day" circle and its tooltip never render here — both are driven off
  // this map (looked up by YYYY-MM-DD), so it must carry the real data, not an
  // empty object.
  const filteredOtherRequestDetails = React.useMemo(() => {
    const result: Record<string, (typeof otherRequestDetailsForTooltip)[string]> =
      {}
    filteredOtherRequestDates.forEach((date) => {
      const key = convertMMDDYYYYToYYYYMMDD(date)
      const detail = otherRequestDetailsForTooltip?.[key]
      if (detail) {
        result[key] = detail
      }
    })
    return result
  }, [filteredOtherRequestDates, otherRequestDetailsForTooltip])

  // Detect if the original request was a birthday request and extract the birthday date
  const originalBirthdayDate = React.useMemo(() => {
    if (!timeOffDetails?.requestedDates) return null

    for (const dateObj of timeOffDetails.requestedDates) {
      if (dateObj.timeOffAllocations && dateObj.timeOffAllocations.length > 0) {
        const isBirthday = dateObj.timeOffAllocations.some(
          (alloc) => alloc.toTypeCd === 'B'
        )
        if (isBirthday) {
          // Convert YYYY-MM-DD to MM/DD/YYYY
          const [year, month, day] = dateObj.date.split('-')
          return `${month}/${day}/${year}`
        }
      }
    }
    return null
  }, [timeOffDetails?.requestedDates])

  // Handle validation errors from AddTimeToRequest.
  // If balance errors are present, suppress generic no-hours banners so users
  // see the actionable root cause for Add Date update attempts.
  const handleValidationError = React.useCallback((errors: string[] | null) => {
    if (!errors || errors.length === 0) {
      setValidationErrors(errors)
      return
    }

    const hasFloatBalance = errors.includes(ValidationMessages.FLOAT_BALANCE)
    const normalizedErrors = hasFloatBalance
      ? errors.filter(
          (msg) =>
            msg !== ValidationMessages.NO_HOURS_ENTERED &&
            msg !== ValidationMessages.NO_HOURS_ENTERED_PARTIAL_DAY
        )
      : errors

    setValidationErrors(normalizedErrors)
  }, [])

  const handleDateChange = (_name: string, value: string | string[]) => {
    // Only handle deselections here — additions are handled exclusively by handleDateClick
    // to prevent a date being added twice (once from onChange, once from onDateClick).
    let newValues: string[]
    if (Array.isArray(value)) {
      newValues = value
    } else {
      newValues = value ? [value] : []
    }

    const currentlySelectedCommitted = allSelectedDates.filter((d) =>
      committedDates.includes(d)
    )
    const nextSelectedCommitted = newValues.filter((d) =>
      committedDates.includes(d)
    )
    if (nextSelectedCommitted.length < currentlySelectedCommitted.length) {
      setValidationErrors(null)
      return
    }

    const removedAdditional = additionalDates.filter(
      (d) => !newValues.includes(d)
    )
    if (removedAdditional.length > 0) {
      setAdditionalDates((prev) =>
        prev.filter((d) => !removedAdditional.includes(d))
      )
    }
    setValidationErrors(null)

    if (isAddingMoreDates) {
      // The most recently clicked date, not just any non-committed one — using
      // committedDates alone always matched the FIRST additional date ever
      // picked (it's non-committed too), so editDate got stuck there instead
      // of advancing to whichever new date the user just clicked.
      const newlyAddedDate = newValues.find((d) => !allSelectedDates.includes(d))

      if (newlyAddedDate) {
        setAdditionalDates((prev) => {
          if (!prev.includes(newlyAddedDate)) {
            return [...prev, newlyAddedDate]
          }
          return prev
        })

        setEditMode(true)
        setEditDate(newlyAddedDate)
      }
    }
  }
  const handleDateClick = (clickedDate: string) => {
  const isInRequest = allSelectedDates.includes(clickedDate)

  if (!isInRequest) {
    if (isPastDate(clickedDate)) return
    setAdditionalDates((prev) => [...prev, clickedDate])
  }

  setValidationErrors(null)

  // When adding more dates, don't immediately enter edit mode for each click —
  // let the user accumulate multiple dates first.
  if (isAddingMoreDates) return

  setEditMode(true)
  setEditDate(clickedDate)
  setExitAddTimeReviewSignal((c) => c + 1)
}

  const handleRemoveDateClick = (dateToRemove: string) => {
    // If it's an additionally-added date, remove it from that list
    if (additionalDates.includes(dateToRemove)) {
      setAdditionalDates((prev) => prev.filter((d) => d !== dateToRemove))
    } else if (selectedDates.includes(dateToRemove)) {
      // If it's an original date, mark it as removed
      setRemovedOriginalDates((prev) => [...prev, dateToRemove])
    }
    // Also drop it from committedDates — Datepicker renders any date in
    // committedDates in light blue ("previously reviewed") regardless of
    // whether it's still part of allSelectedDates, so without this a removed
    // date kept showing as committed on the calendar even after its row was
    // gone from the request.
    setCommittedDates((prev) => prev.filter((d) => d !== dateToRemove))
    // Exit edit mode
    setEditMode(false)
    setEditDate(null)
  }

  const handleExitEditMode = () => {
    setEditMode(false)
    setEditDate(null)
  }

  const handleAddTimeReviewChange = (isReview: boolean) => {
    setIsAddTimeReviewVisible(isReview)
    if (isReview && !lastReviewStateRef.current) {
      setCommittedDates((prev) => {
        if (isAddingMoreDates) {
          return [
            ...prev,
            ...additionalDates.filter((date) => !prev.includes(date)),
          ]
        }

        return [...allSelectedDates]
      })

      setIsAddingMoreDates(false)
    }
    lastReviewStateRef.current = isReview
    // Note: Do NOT clear validation errors here - they should persist across
    // review/edit mode transitions. Only the form changing should clear them.
  }

  const handleAddMoreDates = () => {
    setIsAddingMoreDates(true)

  if (isAddTimeReviewVisible) {
      setExitAddTimeReviewSignal((count) => count + 1)
      setIsAddTimeReviewVisible(false)
    }
    // Reset edit mode
    setEditMode(false)
    setEditDate(null)
    setValidationErrors(null)
  }

  const handleCancelRequest = () => {
    setValidationErrors(null)
    if (isAdminMode) {
      navigate(`/admin/requestor/status/${requestId}`)
    } else {
      navigate(`/requestor/status/${requestId}`)
    }
  }

  const handleBackFromEditMode = () => {
    setEditMode(false)
    setEditDate(null)
  }

  if (loading) {
    return <Loader />
  }

  if (error) {
    return (
      <div className="page-container min-h-[60vh] lg:mx-40! desktop:mx-[120px]! tablet:mx-5! mobile:mx-4!">
        <BreadCrumbs
          previousPage="TORT"
          currentPage="Edit & Resubmit Request"
          onPreviousClick={() => navigate('/requestor')}
        />
        <h1 className="h1 mt-6">Error Loading Request</h1>
        <p className="p3 -book mt-4">{error}</p>
        <button
          type="button"
          className="button -primary mt-4"
          onClick={() => navigate('/requestor')}
        >
          Back to Requests
        </button>
      </div>
    )
  }

  // Check if this is a vacation bid request
  const isVacationBidRequest = timeOffDetails
    ? isVacationBid(timeOffDetails)
    : false

  // If it's a vacation bid request, render the vacation bid resubmit component
  if (isVacationBidRequest && timeOffDetails) {
    return (
      <EditAndResubmitVacationBid
        data={timeOffDetails}
        onCancel={() =>
          isAdminMode
            ? navigate(`/admin/requestor/status/${requestId}`)
            : navigate(`/requestor/status/${requestId}`)
        }
        onResubmitSuccess={() =>
          navigate(`${isAdminMode ? '/admin' : ''}/requestor`)
        }
      />
    )
  }

  return (
    <div className="page-container min-h-[60vh] lg:mx-40! desktop:mx-[120px]! tablet:mx-5! mobile:mx-4!">
      <BreadCrumbs
        previousPage="TORT"
        currentPage="Edit & Resubmit Request"
        onPreviousClick={() =>
          navigate(`${isAdminMode ? '/admin' : ''}/requestor`)
        }
      />
      {isAdminMode && (
        <div className="my-8!">
          <ManualTimeOffBanner />
        </div>
      )}
      <h1 className="h1 mb-6">Submitted – Returned Time Off Request</h1>
      {validationErrors &&
        validationErrors.length > 0 &&
        renderEditSubmittedValidationBanners(validationErrors, () =>
          setFocusOtherSubtypeSignal((count) => count + 1)
        )}

      {/* Two-column layout: Calendar on left, Form/Review on right */}
      <div className="flex gap-16! tablet:gap-8! mobile:gap-2! items-start flex-col tablet:flex-row mobile:flex-col">
        {/* Calendar Column */}
        {!isAddTimeReviewVisible && (
          <div className="w-full lg:w-auto mb-6 lg:mb-0">
              <div className="flex items-center">
                <h2 className="h2 text-lg mb-3">Select Dates</h2>
                {/* Colour legend tooltip */}
                <div className="ml-2">
                  <ColorLegendTooltip />
                </div>
              </div>
            <Datepicker
              name="dates"
              label="Select Dates"
              value={allSelectedDates}
              onChange={handleDateChange}
              onDateClick={handleDateClick}
              required
              openBehaviour="calendarView"
              // Keeping this tied to isAddingMoreDates (not just editMode/editDate)
              // matters: editMode flips true as soon as the first new date is picked
              // (see handleDateChange below), and Datepicker hard-blocks all further
              // clicks whenever isEditMode is true — so without this, only one date
              // could ever be added per Add More session.
              isEditMode={editMode && !!editDate && !isAddingMoreDates}
              editDate={editDate}
              editModeSelectedDates={allSelectedDates}
              otherSelectedDatesToShow={filteredOtherRequestDates}
              otherRequestDetails={filteredOtherRequestDetails}
              committedDates={committedDates}
              calendarWidth={372}
              calendarHeight={399}
              onMonthYearChange={handleMonthYearChange}
              disableCommittedDateClick={isAddingMoreDates}
            />
          </div>
        )}

        {/* Form/Review Table Column */}
        <div className="w-full lg:flex-1">
          <div className="mb-6">
            <h2 className="h2 mb-2">Edit Your Request for Resubmission</h2>
            
			{isAddTimeReviewVisible && (
        <div>
          <p className="p3 -book">
              To add a date, select the Add date to request button. To edit or remove a date, you may use the table below.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="button -secondary block w-full! desktop:w-auto!"
                onClick={handleAddMoreDates}
              >
                Add date to request
              </button>
            </div>
            </div>
			)}
          </div>
          {allSelectedDates.length > 0 ? (
            <AddTimeToRequest
              selectedDates={allSelectedDates}
              committedDates={committedDates}
              editMode={editMode}
              startInReview={true}
              isResubmitFlow={true}
              isVacationBidRequest={isVacationBidRequest}
              isBirthdayRequest={!!originalBirthdayDate}
              exitAddTimeReviewSignal={exitAddTimeReviewSignal}
              focusOtherSubtypeSignal={focusOtherSubtypeSignal}
              onAddTimeReviewChange={handleAddTimeReviewChange}
              onCancelRequest={handleCancelRequest}
              onBackFromEditMode={handleBackFromEditMode}
              editDate={editDate}
              onEditDateClick={(date) => {
                setEditMode(true)
                setEditDate(date)
                setExitAddTimeReviewSignal((c) => c + 1)
              }}
              onRemoveDateClick={handleRemoveDateClick}
              onExitEditMode={handleExitEditMode}
              initialPerDateHoursStorage={initialPerDateHoursStorage}
              initialPerDateDurationStorage={initialPerDateDurationStorage}
              initialPerDateOtherSubTypeStorage={
                initialPerDateOtherSubTypeStorage
              }
              initialPerDateTimeStorage={initialPerDateTimeStorage}
              originalRequestId={requestId}
              initialComments={timeOffDetails?.employeeComments}
              onValidationError={handleValidationError}
              isExempt={isExempt}
              isAddingMoreDates={isAddingMoreDates}
              onAddMoreDates={handleAddMoreDates}
              otherRequestHoursByDate={otherRequestHoursByDate}
              otherPartialDayDates={otherPartialDayDates}
            />
          ) : (
            <p className="p3 -medium pt-2 tablet:pt-4 mobile:pt-2">
              Select a date on the Calendar to add time to your request
            </p>
          )}
        </div>
      </div>
      <TimeOffBalance />
      <EmployeeInfo />
    </div>
  )
}

export default EditSubmittedRequest
